import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PrivacyPolicy = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get current date for the "Last updated" field
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />
      
      <div className="flex-grow pt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-gray-600 mb-6"><strong>Last updated:</strong> {currentDate}</p>

          <p className="mb-6">
            Bit Mentors Inc. ("we", "our", or "us") operates the Link Spaghetti website and services ("Service"). 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you 
            visit our website or use our Service. Please read this policy carefully to understand our views and 
            practices regarding your personal data and how we will treat it.
          </p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">1. Information We Collect</h2>
          <p className="mb-4">We collect the following information when you use our Service:</p>
          <ul className="list-disc pl-8 mb-6 space-y-2">
            <li><strong>Personal Information:</strong> Name, email address, profile photos, banner photos.</li>
            <li><strong>User Content:</strong> Links and other content you upload or store through the Service.</li>
            <li><strong>Payment Information:</strong> Payment details processed securely via Stripe. We do not store your payment card details.</li>
            <li><strong>Usage Data:</strong> Information collected automatically through Google Analytics and other tracking technologies, such as IP address, browser type, pages visited, and usage patterns.</li>
          </ul>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">2. How We Use Your Information</h2>
          <p className="mb-4">We use your information to:</p>
          <ul className="list-disc pl-8 mb-6 space-y-2">
            <li>Provide, operate, and maintain the Service.</li>
            <li>Process payments securely through Stripe.</li>
            <li>Send you transactional emails related to your account and service usage.</li>
            <li>Send marketing and promotional emails with your consent, including updates, offers, and newsletters.</li>
            <li>Improve and personalize your experience on our Service.</li>
            <li>Monitor usage and analyze trends using Google Analytics.</li>
            <li>Comply with legal obligations and enforce our Terms and Conditions.</li>
          </ul>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">3. Marketing Emails and Your Choices</h2>
          <p className="mb-6">
            We may send you marketing emails if you have opted in or consented to receive them. You can unsubscribe 
            at any time by following the unsubscribe link in the email or by contacting us directly at 
            support@linkspaghetti.com.
          </p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">4. Sharing and Disclosure of Your Information</h2>
          <p className="mb-4">We do not sell your personal data. We may share your information with:</p>
          <ul className="list-disc pl-8 mb-6 space-y-2">
            <li><strong>Service Providers:</strong> Trusted third parties such as Stripe (payment processing), Google Analytics (analytics), and email service providers who help us operate the Service.</li>
            <li><strong>Legal Requirements:</strong> When required by law, or to protect our rights and safety or those of others.</li>
          </ul>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">5. Data Storage and Security</h2>
          <p className="mb-6">
            Your data is stored securely using MongoDB and other infrastructure providers. We implement appropriate 
            technical and organizational measures to protect your personal information from unauthorized access, 
            disclosure, alteration, or destruction.
          </p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">6. Your Rights</h2>
          <p className="mb-4">
            Depending on your jurisdiction (including the EU GDPR and Canadian PIPEDA), you have rights regarding 
            your personal data, including:
          </p>
          <ul className="list-disc pl-8 mb-6 space-y-2">
            <li>Accessing your personal information.</li>
            <li>Requesting correction or deletion of your data.</li>
            <li>Withdrawing consent for marketing communications.</li>
            <li>Data portability requests.</li>
          </ul>
          <p className="mb-6">
            To exercise these rights, please contact us at support@linkspaghetti.com. We will respond in accordance 
            with applicable law.
          </p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">7. Cookies and Tracking Technologies</h2>
          <p className="mb-6">
            We use cookies and similar tracking technologies to enhance your experience and collect usage data 
            through Google Analytics. You can manage or disable cookies through your browser settings, but this 
            may affect functionality.
          </p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">8. International Users</h2>
          <p className="mb-6">
            Link Spaghetti is operated internationally including Canada, Ireland, the European Union, and India. 
            By using our Service, you consent to the transfer, storage, and processing of your information in 
            these locations and in accordance with applicable data protection laws.
          </p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">9. Children's Privacy</h2>
          <p className="mb-6">
            Our Service is not intended for anyone under 18 years of age. We do not knowingly collect personal 
            information from children under 18.
          </p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">10. Changes to This Privacy Policy</h2>
          <p className="mb-6">
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the 
            new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this 
            Policy periodically.
          </p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">11. Contact Us</h2>
          <p className="mb-6">
            If you have any questions about this Privacy Policy or your data, please contact us at:
          </p>
          <p className="mb-6">
            <strong>Email:</strong> <a href="mailto:support@linkspaghetti.com" className="text-purple-600 hover:text-purple-800">support@linkspaghetti.com</a><br />
            <strong>Address:</strong> Bit Mentors Inc., Ottawa, Canada
          </p>

          <div className="mt-12 pt-6 border-t border-gray-200 text-center text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Bit Mentors Inc. All rights reserved.
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
